import { useAppDispatch, useAppSelector } from "app/hooks"
import {
  scanMeal,
  processImage,
  removeUploadedImage,
  resetMealState,
  setStep,
  uploadImage,
} from "../mealSlice"
import { LogMealStepType } from "../mealSlice.types"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useEffect } from "react"
import PATHS from "router/paths"
import { DATE_PARAM_FORMAT } from "config"
import { resetSearchedFoodsState } from "features/food/foodSlice"
import { LogMealType } from "model/food/types/base.types"
import dayjs from "dayjs"
import { createImageChangeHandler } from "utils/imageConverter"

export const useScanMeal = () => {
  const scanMealLoading = useAppSelector((state) => state.meal.scanMealLoading)
  const scanMealFailed = useAppSelector((state) => state.meal.scanMealFailed)
  const file = useAppSelector((state) => state.meal.file)
  const imageId = useAppSelector((state) => state.meal.imageId)
  const displayImageType = useAppSelector(
    (state) => state.meal.displayImageType,
  )

  const dispatch = useAppDispatch()

  const handleScanMeal = () => {
    if (displayImageType === "base64" && file) {
      dispatch(scanMeal({ image: file }))
    }

    if (displayImageType === "url" && imageId) {
      dispatch(scanMeal({ imageId }))
    }
  }

  return {
    scanMealLoading,
    scanMealFailed,
    handleScanMeal,
  }
}

export const useUploadImage = () => {
  const imageBase64Encoded = useAppSelector(
    (state) => state.meal.imageBase64Encoded,
  )

  const dispatch = useAppDispatch()

  const onUploadImage = (base64: string, file: File) => {
    dispatch(uploadImage({ base64, file }))
  }

  const onRemoveUploadedImage = () => {
    dispatch(removeUploadedImage())
  }

  return {
    imageBase64Encoded,
    onUploadImage,
    onRemoveUploadedImage,
  }
}

export const useResetMealState = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(resetMealState())
    }
  }, [dispatch])
}

export const useProcessImage = () => {
  const dispatch = useAppDispatch()

  const imageUrl = useAppSelector((state) => state.meal.imageUrl)
  const processImageLoading = useAppSelector(
    (state) => state.meal.processImageLoading,
  )

  const displayImageType = useAppSelector(
    (state) => state.meal.displayImageType,
  )

  const handleProcessImage = (file: File) => {
    dispatch(processImage(file))
  }

  return {
    imageUrl,
    processImageLoading,
    handleProcessImage,
    displayImageType,
  }
}

export const useLogMealStep = () => {
  const dispatch = useAppDispatch()

  const handleSetStep = (step: LogMealStepType) => {
    dispatch(setStep(step))
  }

  return handleSetStep
}

export const useHomePathWithSelectedDate = () => {
  const selectedDate = useAppSelector((state) => state.home.selectedDate)

  const date = selectedDate ?? dayjs()

  return (
    PATHS.app.root +
    "?" +
    new URLSearchParams({
      date: date.format(DATE_PARAM_FORMAT) ?? "",
    }).toString()
  )
}

export const useBackToHome = () => {
  const navigate = useNavigate()
  const homePath = useHomePathWithSelectedDate()
  return () => {
    navigate(homePath)
  }
}

export const useSetMealTypeParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const mealType = searchParams.get("mealType") ?? undefined

  const date = searchParams.get("date")

  const setMealType = (mealType?: LogMealType) => {
    setSearchParams(
      { mealType: mealType ? mealType.toLowerCase() : "", date: date ?? "" },
      { replace: true },
    )
  }

  const isMealTypeValid =
    mealType &&
    ["breakfast", "lunch", "dinner", "snack"].includes(mealType.toLowerCase())

  const mealTypeValue = isMealTypeValid
    ? (mealType.toUpperCase() as LogMealType)
    : undefined

  return {
    mealTypeValue,
    setMealType,
  }
}

export const useMealTypeQueryParams = () => {
  const [searchParams] = useSearchParams()
  const mealType = searchParams.get("mealType") ?? ""
  const mealTypeQueryParam = mealType ? `?mealType=${mealType}` : ""

  return mealTypeQueryParam
}

export const useImageUploader = () => {
  const { onUploadImage, imageBase64Encoded, onRemoveUploadedImage } =
    useUploadImage()
  const {
    processImageLoading,
    handleProcessImage,
    imageUrl,
    displayImageType,
  } = useProcessImage()

  const onImageChange = createImageChangeHandler({
    onProcessImage: handleProcessImage,
    onUploadImage,
  })

  const displayImageUrl =
    displayImageType === "base64" ? imageBase64Encoded : imageUrl

  return {
    onImageChange,
    onRemoveUploadedImage,
    processImageLoading,
    displayImageUrl,
  }
}

export const useResetSearchFoodsState = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(resetSearchedFoodsState())
  }, [])
}
